import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import AdminShell from "./AdminShell";
import { dialog } from "../Common/TreasuryDialog";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "";

// ── Design tokens (matching Treasury theme) ───────────────────────────────────
const DARK   = "#1a3c2e";
const GOLD   = "#c9a84c";
const GREEN  = "#16a34a";
const RED    = "#dc2626";
const BORDER = "#e5e7eb";
const MUTED  = "#6b7280";
const MUTED2 = "#9ca3af";
const BG     = "#f4f6f4";
const CARD   = "#ffffff";

const EMPTY_FORM = { title: "", summary: "", body: "", type: "general", propertyId: "", externalUrl: "", imageUrl: "" };

// ── Small helpers ─────────────────────────────────────────────────────────────
const Badge = ({ type }) => (
    <span style={{
        display: "inline-block", padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
        background: type === "general" ? "#dbeafe" : "#dcfce7",
        color: type === "general" ? "#1d4ed8" : "#15803d",
    }}>
        {type === "general" ? "General" : "Property"}
    </span>
);

const Input = ({ label, ...props }) => (
    <div style={{ marginBottom: 14 }}>
        {label && <label style={{ fontSize: 12, fontWeight: 600, color: MUTED, textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 5 }}>{label}</label>}
        <input {...props} style={{ width: "100%", padding: "10px 12px", border: `1.5px solid ${BORDER}`, borderRadius: 8, fontSize: 14, outline: "none", boxSizing: "border-box", ...props.style }} />
    </div>
);

const Textarea = ({ label, ...props }) => (
    <div style={{ marginBottom: 14 }}>
        {label && <label style={{ fontSize: 12, fontWeight: 600, color: MUTED, textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 5 }}>{label}</label>}
        <textarea {...props} style={{ width: "100%", padding: "10px 12px", border: `1.5px solid ${BORDER}`, borderRadius: 8, fontSize: 14, outline: "none", resize: "vertical", boxSizing: "border-box", minHeight: 90, ...props.style }} />
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
const AdminNews = () => {
    const { auth } = useSelector((s) => s);
    const token = auth?.data?.access_token || auth?.data?.accesstoken;

    const [articles, setArticles]       = useState([]);
    const [properties, setProperties]   = useState([]);
    const [form, setForm]               = useState(EMPTY_FORM);
    const [loading, setLoading]         = useState(false);
    const [saving, setSaving]           = useState(false);
    const [filterType, setFilterType]   = useState("all");
    const [editId, setEditId]           = useState(null);
    const [showForm, setShowForm]       = useState(false);

    // ── Fetch news ────────────────────────────────────────────────────────────
    const fetchNews = useCallback(async () => {
        if (!token) return;  // wait until auth resolves
        setLoading(true);
        try {
            const params = filterType !== "all" ? `?type=${filterType}` : "";
            const res = await axios.get(`${API}/api/admin/news${params}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setArticles(res.data.data || []);
        } catch (err) {
            console.error("[AdminNews] fetchNews error:", err?.response?.status, err?.response?.data || err.message);
        }
        setLoading(false);
    }, [token, filterType]);

    // ── Fetch properties for dropdown ─────────────────────────────────────────
    const fetchProperties = useCallback(async () => {
        if (!token) return;  // wait until auth resolves
        try {
            const res = await axios.get(`${API}/api/get_property`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
            setProperties(list);
        } catch (err) {
            console.error("[AdminNews] fetchProperties error:", err?.response?.status, err?.response?.data || err.message);
        }
    }, [token]);

    useEffect(() => { fetchNews(); }, [fetchNews]);
    useEffect(() => { fetchProperties(); }, [fetchProperties]);

    // ── Form handlers ─────────────────────────────────────────────────────────
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(f => ({ ...f, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.title.trim() || !form.summary.trim()) {
            dialog.error("Validation", "Title and summary are required.");
            return;
        }
        setSaving(true);
        try {
            const payload = { ...form };
            if (payload.type !== "property") payload.propertyId = "";
            const authHeaders = { Authorization: `Bearer ${token}` };
            if (editId) {
                await axios.put(`${API}/api/admin/news/${editId}`, payload, { headers: authHeaders });
                dialog.success("Updated", "Article updated successfully.");
            } else {
                await axios.post(`${API}/api/admin/news`, payload, { headers: authHeaders });
                dialog.success("Published", "Article published successfully.");
            }
            setForm(EMPTY_FORM);
            setEditId(null);
            setShowForm(false);
            fetchNews();
        } catch {
            dialog.error("Error", "Failed to save article. Please try again.");
        }
        setSaving(false);
    };

    const handleEdit = (article) => {
        setForm({
            title: article.title || "",
            summary: article.summary || "",
            body: article.body || "",
            type: article.type || "general",
            propertyId: article.propertyId?._id || article.propertyId || "",
            externalUrl: article.externalUrl || "",
            imageUrl: article.imageUrl || "",
        });
        setEditId(article._id);
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleDelete = async (id) => {
        const confirmed = await dialog.confirm("Delete Article", "Are you sure you want to delete this article? This cannot be undone.");
        if (!confirmed) return;
        try {
            await axios.delete(`${API}/api/admin/news/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            fetchNews();
        } catch {
            dialog.error("Error", "Failed to delete article.");
        }
    };

    const handleTogglePublish = async (article) => {
        try {
            await axios.put(`${API}/api/admin/news/${article._id}`, { published: !article.published }, { headers: { Authorization: `Bearer ${token}` } });
            fetchNews();
        } catch { /* ignore */ }
    };

    const cancelEdit = () => { setForm(EMPTY_FORM); setEditId(null); setShowForm(false); };

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <AdminShell pageTitle="News Management">
            <div style={{ maxWidth: 1100, margin: "0 auto" }}>

                {/* ── Page header ──────────────────────────────────────────── */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
                    <div>
                        <h1 style={{ fontSize: 22, fontWeight: 800, color: DARK, margin: 0 }}>News Management</h1>
                        <p style={{ fontSize: 13, color: MUTED, margin: "4px 0 0" }}>Publish general news to the platform News page, or attach news to specific properties.</p>
                    </div>
                    <button
                        onClick={() => { cancelEdit(); setShowForm(s => !s); }}
                        style={{ padding: "10px 20px", background: DARK, color: GOLD, border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                        {showForm ? "✕ Cancel" : "+ New Article"}
                    </button>
                </div>

                {/* ── Compose form ─────────────────────────────────────────── */}
                {showForm && (
                    <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "24px 28px", marginBottom: 28, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                        <h3 style={{ fontSize: 16, fontWeight: 700, color: DARK, margin: "0 0 20px" }}>
                            {editId ? "✏️ Edit Article" : "📝 New Article"}
                        </h3>
                        <form onSubmit={handleSubmit}>
                            {/* Type toggle */}
                            <div style={{ marginBottom: 18 }}>
                                <label style={{ fontSize: 12, fontWeight: 600, color: MUTED, textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 8 }}>Article Type</label>
                                <div style={{ display: "flex", gap: 0, background: "#f3f4f6", borderRadius: 10, padding: 4, width: "fit-content" }}>
                                    {["general", "property"].map(t => (
                                        <button key={t} type="button"
                                            onClick={() => setForm(f => ({ ...f, type: t }))}
                                            style={{
                                                padding: "8px 20px", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer",
                                                background: form.type === t ? DARK : "transparent",
                                                color: form.type === t ? GOLD : MUTED,
                                                transition: "all 0.15s",
                                            }}>
                                            {t === "general" ? "🌐 General (News)" : "🏢 Property-Specific"}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Property selector (only for property type) */}
                            {form.type === "property" && (
                                <div style={{ marginBottom: 14 }}>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: MUTED, textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 5 }}>Select Property</label>
                                    <select name="propertyId" value={form.propertyId} onChange={handleChange}
                                        style={{ width: "100%", padding: "10px 12px", border: `1.5px solid ${BORDER}`, borderRadius: 8, fontSize: 14, outline: "none", background: "#fff", cursor: "pointer" }}>
                                        <option value="">— Choose a property —</option>
                                        {properties.map(p => (
                                            <option key={p._id} value={p._id}>{p.title}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                                <Input label="Title *" name="title" placeholder="Article headline" value={form.title} onChange={handleChange} required />
                                <Input label="Cover Image URL" name="imageUrl" placeholder="https://..." value={form.imageUrl} onChange={handleChange} />
                            </div>
                            <Textarea label="Summary *" name="summary" placeholder="Short description shown in the news card (2–3 sentences)" value={form.summary} onChange={handleChange} required style={{ minHeight: 70 }} />
                            <Textarea label="Full Body (optional)" name="body" placeholder="Full article content (supports plain text)" value={form.body} onChange={handleChange} style={{ minHeight: 120 }} />
                            <Input label="External URL (optional)" name="externalUrl" placeholder="Link to full article on external site" value={form.externalUrl} onChange={handleChange} />

                            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 8 }}>
                                <button type="button" onClick={cancelEdit}
                                    style={{ padding: "10px 20px", background: "none", border: `1.5px solid ${BORDER}`, borderRadius: 8, fontSize: 14, fontWeight: 600, color: MUTED, cursor: "pointer" }}>
                                    Cancel
                                </button>
                                <button type="submit" disabled={saving}
                                    style={{ padding: "10px 24px", background: saving ? "#e5e7eb" : `linear-gradient(135deg, ${DARK}, #1a5c3a)`, color: saving ? MUTED : GOLD, border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer" }}>
                                    {saving ? "Saving…" : (editId ? "Update Article" : "Publish Article")}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* ── Filter tabs ───────────────────────────────────────────── */}
                <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
                    {["all", "general", "property"].map(t => (
                        <button key={t} onClick={() => setFilterType(t)}
                            style={{
                                padding: "7px 16px", border: `1.5px solid ${filterType === t ? DARK : BORDER}`, borderRadius: 8,
                                background: filterType === t ? DARK : CARD, color: filterType === t ? GOLD : MUTED,
                                fontSize: 13, fontWeight: 600, cursor: "pointer",
                            }}>
                            {t === "all" ? "All" : t === "general" ? "🌐 General" : "🏢 Property"}
                        </button>
                    ))}
                    <span style={{ marginLeft: "auto", fontSize: 13, color: MUTED2, alignSelf: "center" }}>{articles.length} article{articles.length !== 1 ? "s" : ""}</span>
                </div>

                {/* ── Articles list ─────────────────────────────────────────── */}
                {loading ? (
                    <div style={{ textAlign: "center", padding: "48px", color: MUTED2, fontSize: 14 }}>Loading…</div>
                ) : articles.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "60px 24px", background: CARD, borderRadius: 14, border: `1px solid ${BORDER}` }}>
                        <div style={{ fontSize: 40, marginBottom: 12 }}>📰</div>
                        <p style={{ fontSize: 15, fontWeight: 600, color: DARK, margin: "0 0 6px" }}>No articles yet</p>
                        <p style={{ fontSize: 13, color: MUTED2, margin: 0 }}>Click "+ New Article" to publish your first news article.</p>
                    </div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                        {articles.map(article => (
                            <div key={article._id} style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "18px 22px", display: "flex", gap: 18, alignItems: "flex-start", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
                                {/* Cover image thumbnail */}
                                {article.imageUrl && (
                                    <img src={article.imageUrl} alt="" onError={e => e.target.style.display = "none"}
                                        style={{ width: 80, height: 60, objectFit: "cover", borderRadius: 8, flexShrink: 0 }} />
                                )}
                                {/* Content */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 5, flexWrap: "wrap" }}>
                                        <Badge type={article.type} />
                                        {article.type === "property" && article.propertyId && (
                                            <span style={{ fontSize: 12, color: MUTED, background: "#f3f4f6", padding: "2px 8px", borderRadius: 6 }}>
                                                🏢 {article.propertyId.title || "Property"}
                                            </span>
                                        )}
                                        {!article.published && (
                                            <span style={{ fontSize: 11, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", padding: "2px 8px", borderRadius: 6, fontWeight: 600 }}>Draft</span>
                                        )}
                                        <span style={{ marginLeft: "auto", fontSize: 11.5, color: MUTED2 }}>
                                            {new Date(article.createdAt).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})}
                                        </span>
                                    </div>
                                    <h4 style={{ fontSize: 15, fontWeight: 700, color: "#111827", margin: "0 0 5px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{article.title}</h4>
                                    <p style={{ fontSize: 13, color: MUTED, margin: 0, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{article.summary}</p>
                                </div>
                                {/* Actions */}
                                <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "center" }}>
                                    <button onClick={() => handleTogglePublish(article)}
                                        title={article.published ? "Unpublish" : "Publish"}
                                        style={{ padding: "6px 12px", border: `1.5px solid ${BORDER}`, borderRadius: 7, background: "none", fontSize: 12, fontWeight: 600, color: article.published ? GREEN : MUTED, cursor: "pointer" }}>
                                        {article.published ? "✓ Live" : "Draft"}
                                    </button>
                                    <button onClick={() => handleEdit(article)}
                                        style={{ padding: "6px 12px", border: `1.5px solid ${BORDER}`, borderRadius: 7, background: "none", fontSize: 12, fontWeight: 600, color: DARK, cursor: "pointer" }}>
                                        Edit
                                    </button>
                                    <button onClick={() => handleDelete(article._id)}
                                        style={{ padding: "6px 12px", border: `1.5px solid #fca5a5`, borderRadius: 7, background: "none", fontSize: 12, fontWeight: 600, color: RED, cursor: "pointer" }}>
                                        Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </AdminShell>
    );
};

export default AdminNews;
